import {
  field,
  date,
  children,
  writer,
  relation,
  lazy,
  text,
} from "@nozbe/watermelondb/decorators";
import { STOCK_SCHEMA, INVENTORY_SCHEMA } from "../schema";
import { uuid } from "../../oscar-pos-core/constants";
import { Model, Q } from "@nozbe/watermelondb";

export default class Stock extends Model {
  static table = STOCK_SCHEMA;

  static associations = {
    stock_categories: { type: "belongs_to", key: "category_id" },
    business: { type: "belongs_to", key: "business_id" },
    pos_order_lines: { type: "has_many", foreignKey: "stock_id" },
    sale_order_line: { type: "has_many", foreignKey: "stock_id" },
    stock_moves: { type: "has_many", foreignKey: "stock_id" },
    orders: { type: "has_many", foreignKey: "stock_id" },
  };

  @field("minimum_order_quantity") minimum_order_quantity;
  @field("wholesale_price") wholesale_price;
  @field("unit_conversion") unit_conversion;
  @text("secondary_unit") secondary_unit;
  @field("selling_price") selling_price;
  @field("batch_number") batch_number;
  @field("cost_price") cost_price;
  @text("catalog_id") catalog_id;
  @field("is_synced") is_synced;
  @field("inclusive") inclusive;
  @field("low_stock") low_stock;
  @text("image_url") image_url;
  @field("archived") archived;
  @field("bar_code") bar_code;
  @field("barcode") barcode;
  @field("stock") stock;
  @text("name") name;
  @text("unit") unit;
  @field("imei") imei;
  @field("tax") tax;
  @field("sku") sku;


  @date("manufacturing_date") manufacturing_date;
  @date("expiry_alert") expiry_alert;
  @date("expiry_date") expiry_date;
  @date("created_at") created_at;

  @relation("stock_categories", "category_id") stock_categories;
  @relation("business", "business_id") business;

  @children("pos_order_lines") pos_order_lines;
  @children("sale_order_line") sale_order_line;
  @children("stock_moves") stock_moves;
  @children("orders") orders;

  @lazy
  getInvByDate = (startDate, endDate, limit, offset) => {
    let query = this.stock_moves;

    if (startDate && endDate) {
      query = this.stock_moves.extend(
        Q.where("transacted_at", Q.gte(startDate)),
        Q.where("transacted_at", Q.lte(endDate)),
      );
    }
    // Apply limit and offset if provided
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @lazy
  getOrdersByDate = (startDate, endDate, limit, offset) => {
    let query = this.orders;

    // Apply date filters only if both startDate and endDate are provided
    if (startDate && endDate) {
      query = query.extend(
        Q.where("transacted_at", Q.gte(startDate)),
        Q.where("transacted_at", Q.lte(endDate)),
      );
    }

    // Apply limit and offset if provided
    if (limit !== undefined) {
      query = query.extend(Q.take(limit));
    }

    if (offset !== undefined) {
      query = query.extend(Q.skip(offset));
    }

    return query;
  };

  @writer async updateStockQuantity(amount) {
    await this.update((st) => {
      st.stock = amount;
    });
  }

  @writer async updateStock(details) {
    await this.update((st) => {
      details.category && st.stock_categories.set(details.category);
      st.selling_price = details.selling_price;
      details.unit && (st.unit = details.unit);
      st.cost_price = details.cost_price;
      st.bar_code = details.bar_code;
      st.stock = details.stock;
      st.name = details.name;
    });
  }

  @writer async createEntryInInventory(details) {
    await this.collections.get(INVENTORY_SCHEMA).create((inventory) => {
      inventory._raw.id = uuid();
      inventory.stock.set(this);
      inventory.transacted_at = details.transacted_at;
      inventory.quantity = details.quantity;
      inventory.name = details.name;
      inventory.type = details.type;
    });
  }

  @writer async archiveStock() {
    await this.update((st) => {
      st.archived = true;
    });
  }

  @writer async updateStockImage(url) {
    await this.update((stock) => {
      stock.image_url = url;
    });
  }

  @writer async createInventory(details) {
    try {
      const batchResult = await this.batch(
        this.collections.get(INVENTORY_SCHEMA).prepareCreate((inventory) => {
          inventory.stock.set(this);
          inventory.is_secondary_unit = details.is_secondary_unit;
          inventory.unit_conversion = details.unit_conversion;
          inventory.transacted_at = details.transacted_at;
          inventory.quantity = details.quantity;
          inventory.price = details.price;
          inventory.type = details.type;
          inventory.name = details.name;
          inventory._raw.id = uuid();
        }),
        this.prepareUpdate((item) => {
          item.stock = details.stock;
        }),
      );
      return batchResult;
    } catch (error) {
      console.error("Error in createInventory batch operation:", error);
      throw error;
    }
  }

  @writer async updateItem(details) {
    return await this.update((stock) => {
      stock.minimum_order_quantity = details.minimum_order_quantity;
      stock.manufacturing_date = details.manufacturing_date;
      stock.unit_conversion = details.unit_conversion;
      stock.wholesale_price = details.wholesale_price;
      stock.secondary_unit = details.secondary_unit;
      stock.selling_price = details.selling_price;
      stock.batch_number = details.batch_number;
      stock.expiry_alert = details.expiry_alert;
      stock.expiry_date = details.expiry_date;
      stock.cost_price = details.cost_price;
      stock.image_url = details.image_url;
      stock.low_stock = details.low_stock;
      stock.barcode = details.barcode;
      stock.stock = details.stock;
      stock.name = details.name;
      stock.unit = details.unit;
      stock.imei = details.imei;
      stock.sku = details.sku;
      
    });
  }
}
